import { reactive } from 'vue';
import { resolveApiBaseUrl } from '@/utils/auth/apiBaseUrl';
import { log } from '@/utils/core/vueLogger';
import { CAUSE, MODE, isOnlineMode, modeAfterProbe, shouldReprobe } from '@/utils/domain/connectivityRules';

// Single source of truth of the online/offline state (md/CONNECTIVITY.md). Vue code reads isOnline() or
// useNetworkStatus(); the legacy reads the mode through public/m.eagle/js/connectivity.js and reacts to
// evtConnectivityChanged (both wired by src/iframe/legacyConnectivity.js).
//
// Detection replaces js/offline.js: browser online/offline events, settled 1.5 s like its queue, and a no-cors
// request to the environment API (the one apiBaseUrl.js resolves for the legacy and Vue calls). While 'down' or
// 'auto_down' and the tab is visible, the API is probed again every 30 s; the manual mode is only left on request.
//
// The API has no health endpoint: the probe is a HEAD on its static Swagger page (200 on every environment, no
// database work, no 404 in the console). A no-cors response is opaque, so any answer means reachable.
const PROBE_PATH = 'swagger/index.html';

const SETTLE_MS = 1500;
const PROBE_TIMEOUT_MS = 5000;
const REPROBE_MS = 30000;

const browserOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false;
const initialMode = () => (browserOnline() ? MODE.UP : MODE.DOWN);

export const connectivity = reactive({ mode: initialMode(), online: isOnlineMode(initialMode()), cause: null, offlineAllowed: true });

const listeners = new Set();
let started = false;
let probe = probeApi;
let inflight = null;
let settleTimer = null;
let reprobeTimer = null;

async function probeApi() {
	if (!browserOnline()) return false;
	const base = resolveApiBaseUrl(window.location.hostname, import.meta.env.VITE_API_URL || '');
	if (!base) return true;

	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
	try {
		await fetch(`${base}/${PROBE_PATH}?_=${Date.now()}`, { method: 'HEAD', mode: 'no-cors', cache: 'no-store', credentials: 'omit', signal: controller.signal });
		return true;
	} catch {
		return false;
	} finally {
		clearTimeout(timer);
	}
}

export const getConnectivityMode = () => connectivity.mode;
export const isOnline = () => isOnlineMode(connectivity.mode);

export function onConnectivityChange(listener) {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

function scheduleReprobe() {
	clearTimeout(reprobeTimer);
	reprobeTimer = null;
	if (!started || !shouldReprobe(connectivity.mode)) return;
	reprobeTimer = setTimeout(() => {
		reprobeTimer = null;
		if (document.visibilityState === 'visible') checkConnectivity();
		else scheduleReprobe();
	}, REPROBE_MS);
}

export function setConnectivityMode(mode, cause) {
	const previous = connectivity.mode;
	if (mode === previous) return false;

	Object.assign(connectivity, { mode, online: isOnlineMode(mode), cause });
	log('info', 'Connectivity', `${previous} → ${mode} (${cause})`);
	scheduleReprobe();

	const change = { state: mode, previous, cause };
	listeners.forEach((listener) => {
		try {
			listener(change);
		} catch (error) {
			log('error', 'Connectivity', `listener failed: ${error.message}`);
		}
	});
	return true;
}

function probeOnce() {
	if (!inflight) {
		inflight = Promise.resolve()
			.then(() => probe())
			.catch(() => false)
			.then((reachable) => {
				inflight = null;
				return Boolean(reachable);
			});
	}
	return inflight;
}

export async function checkConnectivity() {
	const reachable = await probeOnce();
	if (!setConnectivityMode(modeAfterProbe(connectivity.mode, reachable), CAUSE.NETWORK)) scheduleReprobe();
	return reachable;
}

export function scheduleConnectivityCheck(delay = SETTLE_MS) {
	clearTimeout(settleTimer);
	settleTimer = setTimeout(() => {
		settleTimer = null;
		checkConnectivity();
	}, delay);
}

// A network error on a Vue API call: confirm it with a probe rather than trusting a single request.
export function reportNetworkFailure() {
	if (isOnline()) scheduleConnectivityCheck();
}

export const enterOffline = (mode, cause) => setConnectivityMode(mode, cause);

// Coming back from 'manual_down' / 'auto_down': online only if the API answers.
export async function leaveOffline() {
	const reachable = await probeOnce();
	setConnectivityMode(reachable ? MODE.UP : MODE.DOWN, reachable ? CAUSE.MANUAL : CAUSE.NETWORK);
	return reachable;
}

const onBrowserChange = () => scheduleConnectivityCheck();
const onVisibilityChange = () => {
	if (document.visibilityState === 'visible' && shouldReprobe(connectivity.mode)) checkConnectivity();
};

// main.js, before the legacy iframe is created. `options.probe` replaces the API request (tests).
export function startConnectivity(options = {}) {
	if (options.probe) probe = options.probe;
	if (started) return;
	started = true;
	window.addEventListener('online', onBrowserChange);
	window.addEventListener('offline', onBrowserChange);
	document.addEventListener('visibilitychange', onVisibilityChange);
	checkConnectivity();
}

export function stopConnectivity() {
	if (started) {
		window.removeEventListener('online', onBrowserChange);
		window.removeEventListener('offline', onBrowserChange);
		document.removeEventListener('visibilitychange', onVisibilityChange);
	}
	started = false;
	clearTimeout(settleTimer);
	clearTimeout(reprobeTimer);
	settleTimer = reprobeTimer = inflight = null;
	probe = probeApi;
	Object.assign(connectivity, { mode: initialMode(), online: isOnlineMode(initialMode()), cause: null, offlineAllowed: true });
}
